<template>
  <div class="">
    <!-- Loading state -->
    <div v-if="labelsAreLoading">
      <Skeleton
        backgroundColor="bg-gray-100"
        width="w-72"
        height="h-244"
      />
    </div>

    <!-- Error state - only show in buyflow -->
    <div v-else-if="!labelAvailable && isBuyflow" class="p-4 border border-red-200 rounded bg-red-50">
      <p class="text-red-700">
        We are experiencing technical difficulties. 
        Please visit your MyAccount online portal later to view a label describing key features of your Breezeline's service option.
      </p>
    </div>

    <!-- Success state -->
    <div v-else-if="showBroadbandLabels && plan?.labels" :class="{ 'buyflow': isBuyflow, 'non-buyflow': !isBuyflow }">
      <div v-html="getLabelHtml"></div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Skeleton from './Skeleton.vue'

export default {
  name: "BroadbandLabel",
  components: {
    Skeleton,
  },
  props: {
      showBroadbandLabels: {
          type: Boolean,
          default: true
      },
      offerTitle: {
          type: String,
          default: ''
      },
      selectedLabel: {
          type: String
      },
      isBuyflow: {
          type: Boolean,
          default: false
      },
      name: {
          type: String,
          default: null,
      },
      promoPrice: {
          type: Number,
          default: 99,
      },
      hsdPromoPrice: {
          type: Number,
          default: null,
      },
      hsdFullPrice: {
          type: Number,
          default: null,
      },
      promoMonths: {
          default: "12",
      },
      fullPrice: {
          type: Number,
          default: 999,
      },
      download: {
          default: "1000",
      },
      upload: {
          type: String,
          default: null,
      },
      labelSpeed: {
          type: String,
          default: "1000",
      },
      allData: {
          type: Object,
          default: () => ({}) 
      }
  },

  data() {
      return {
          pageLang: '',
      }
  },

  computed: {
    ...mapState('BroadbandLabels', [
      'labelData', 
      'labelsAreLoading', 
      'labelAvailable',
      'labelRequest'
    ]),
    
    ...mapState({
      offerId: state => state.meta.offerId,
      marketType: state => state.meta.marketType,
      stateOrProvince: state => state.meta.stateOrProvince,
    }),

    plan() {
      const result = this.getProductLabelData(this.name);
      return result;
    },

    getLabelHtml() {
      return this.plan?.labels[0]?.labelHtml;
    }
  },

  methods: {
    ...mapActions('BroadbandLabels', [
      'fetchBroadbandLabel', 
      'setLoadingState', 
      'setLabelStatus',
    ]),

    getProductLabelData(offerName) {
      return this.labelData.flat().find(data =>
        data.labels.some(label => label.name === offerName)
      ) || false;
    },

    async fetchLabel() {
      this.setLoadingState(true);
      const hsdFullPrice = this.hsdFullPrice || 0;
      const hsdPromoPrice = this.hsdPromoPrice || 0;

      let installations = this.installInfo();

      let oneTimeFees = {
        installationType: []
      };

      if (installations.length > 0) {
        installations.forEach((installation) => {
          oneTimeFees.installationType.push({
            title: installation.title,
            value: this.toCent(installation.price)
          });
        });
      }

      try {
        const payload = {
          planName: this.name,
          tier: this.download,
          downloadSpeed: this.download * 1000,
          uploadSpeed: this.upload * 1000,
          promotionalPriceDuration: this.promoMonths,
          price: this.toCent(hsdFullPrice),
          promotionalPrice: this.toCent(hsdPromoPrice),
          offerId: this.offerId.toString(),
          marketCode: this.marketType,
          hasPromotionalPrice: true,
          language: this.getLanguage(),
          additionalCharges: {
            providerMonthlyFees: {
              equipmentLease: {
                title: "Equipment Lease",
                value: this.toCent(this.equipmentPrice())
              },
              wifiExtender: {
                title: "Optional WiFi Extender",
                value: this.toCent(this.wifiExtenderPrice())
              },
              paperStatementFee: {
                title: "Paper Statement Fee",
                value: this.toCent(this.paperStatementFee())
              }
            },
            oneTimeFees: oneTimeFees
          }
        }

        await this.fetchBroadbandLabel(payload);
      } catch (error) {
        console.error('Error fetching label:', error);
      } finally {
        this.setLoadingState(false);
      }
    },

    toCent(amount) {
      if (typeof amount === 'string') {
        amount = parseFloat(amount);
      }
      
      if (typeof amount !== 'number' || isNaN(amount)) {
        console.error('Invalid input to toCent function:', amount);
        return 0;
      }

      // Multiply by 100 and round to nearest integer
      return Math.round(amount * 100);
    },

    getLanguage() {
      return this.pageLang === "es" ? "spanish" : "english";
    },

    paperStatementFee() {
      const states = [
        {"state": "CT", "price": "0.00"},
        {"state": "DE", "price": "3.00"},
        {"state": "FL", "price": "3.00"},
        {"state": "MA", "price": "0.00"},
        {"state": "MD", "price": "3.00"},
        {"state": "ME", "price": "0.00"},
        {"state": "NH", "price": "3.00"},
        {"state": "NY", "price": "0.00"},
        {"state": "OH", "price": "3.00"},
        {"state": "PA", "price": "3.00"},
        {"state": "SC", "price": "3.00"},
        {"state": "VA", "price": "3.00"},
        {"state": "WV", "price": "0.00"}
      ];
      const result = states.find(item => item.state === this.stateOrProvince);
      return result ? result.price : "0.00";
    },

    installInfo() {
      try {
        const install = this.allData?.install;
        if (!install?.length) {
          return [{
            title: "Professional Installation",
            price: 99
          }];
        }
        const proInstall = install.find(item => item.name === "Professional Install");

        return proInstall ? [{
          title: "Professional Installation",
          price: proInstall.fullPrice ?? 99
        }] : [{
          title: "Professional Installation",
          price: 99
        }];
      } catch (error) {
        console.error('Error getting install info:', error);
        return [{
          title: "Professional Installation",
          price: 99
        }];
      }
    },

    equipmentPrice() {
      try {
        const equip = this.allData?.equipment;
        if (!equip?.equipment_gateway?.length) {
          return 0;  // Return default if data is missing
        }
        return equip.equipment_gateway[0].fullPrice || 0;
      } catch (error) {
        console.error('Error getting equipment price:', error);
        return 0;
      }
    },

    wifiExtenderPrice() {
      try {
        const wifi = this.allData?.equipment?.equipment_wyw;
        if (!wifi?.length || !wifi[0]?.variants?.equipment_wyw_pods?.length) {
          return 0;  // Return default if data is missing
        }
        return wifi[0].variants.equipment_wyw_pods[0].fullPrice || 0;
      } catch (error) {
        console.error('Error getting WiFi extender price:', error);
        return 0;
      }
    }
  },

  watch: {
    labelData: {
      handler(newVal, oldVal) {
        if (newVal.length === 0 && oldVal.length > 0) {
          this.fetchLabel();
        }
      },
      deep: true
    },
  },

  mounted() {
    this.pageLang = document.querySelector("html").getAttribute("lang");
    
    if (this.getProductLabelData(this.name)) {
      this.setLoadingState(false);
      this.setLabelStatus(true);
    } else {
      this.fetchLabel();
    }
    
  },
}
</script>

<style>
@media (max-width: 1024px) {
  div:has(.buyflow) {
    grid-column: 1 / -1;
  }
}

@media (min-width: 1024px) {
  .buyflow .label-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
  .non-buyflow .label-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
  }
}

.discounts-and-bundles a {
  text-decoration: underline;
}

.text-red-700 {
  color: rgb(185, 28, 28);
}

.bg-red-50 {
  background-color: rgb(254, 242, 242);
}

.border-red-200 {
  border-color: rgb(254, 202, 202);
}
</style>